import React, { useId, useState } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { HelpCircle, Bug, Sparkles, CreditCard, FileText } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '../packages/ui';
import { createTicket, type TicketCategory } from '../packages/api-client/support-api';

const CATEGORIES: { value: TicketCategory; label: string; Icon: LucideIcon }[] = [
    { value: 'question', label: 'Question',  Icon: HelpCircle },
    { value: 'bug',      label: 'Bug',       Icon: Bug },
    { value: 'feature',  label: 'Feature',   Icon: Sparkles },
    { value: 'billing',  label: 'Billing',   Icon: CreditCard },
    { value: 'other',    label: 'Other',     Icon: FileText },
];

/** Prefix of the signed-in user's ticket list query (/help); keyed further by user id. */
export const MY_TICKETS_KEY = ['support', 'tickets', 'mine'] as const;

export const CATEGORY_LABEL: Record<TicketCategory, string> =
    Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label])) as Record<TicketCategory, string>;

const fieldLabel: React.CSSProperties = { fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-muted)' };

/**
 * The support-ticket form (POST /support/tickets), shared by the floating
 * SupportButton and the /help page so both file tickets the same way. A sent
 * ticket refreshes the my-tickets list wherever it was filed from.
 * onBusyChange lets a container refuse to close mid-send.
 */
export function SupportTicketForm({ onSubmitted, onCancel, onBusyChange }: {
    onSubmitted?: () => void; onCancel?: () => void; onBusyChange?: (busy: boolean) => void;
}) {
    const id = useId();
    const qc = useQueryClient();
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [category, setCategory] = useState<TicketCategory>('question');
    const [submitting, setSubmitting] = useState(false);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!subject.trim() || !body.trim()) {
            toast.error('Subject and message are both required.');
            return;
        }
        setSubmitting(true);
        onBusyChange?.(true);
        try {
            await createTicket({ subject: subject.trim(), body: body.trim(), category });
            toast.success('Thanks — we got it. Expect a response within 1 business day.');
            setSubject(''); setBody(''); setCategory('question');
            void qc.invalidateQueries({ queryKey: [...MY_TICKETS_KEY] });
            onSubmitted?.();
        } catch (err: any) {
            const msg = err?.response?.data?.message || 'Couldn\'t send — try again in a moment.';
            toast.error(msg);
        } finally {
            setSubmitting(false);
            onBusyChange?.(false);
        }
    }

    return (
        <form aria-label="Contact support" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <div role="group" aria-labelledby={`${id}-cat`}>
                <span id={`${id}-cat`} style={fieldLabel}>Category</span>
                <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap', marginTop: 4 }}>
                    {CATEGORIES.map(c => (
                        <button
                            key={c.value}
                            type="button"
                            aria-pressed={category === c.value}
                            onClick={() => setCategory(c.value)}
                            style={{
                                padding: '6px 12px', borderRadius: 'var(--radius-sm)',
                                border: `1px solid ${category === c.value ? 'var(--accent)' : 'var(--color-border)'}`,
                                background: category === c.value ? 'rgba(var(--accent-rgb), 0.08)' : 'var(--color-surface-2)',
                                cursor: 'pointer', fontSize: 'var(--font-size-xs)',
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                            }}
                        >
                            <c.Icon size={14} aria-hidden="true" />{c.label}
                        </button>
                    ))}
                </div>
            </div>
            <div>
                <label htmlFor={`${id}-subject`} style={fieldLabel}>Subject</label>
                <input
                    id={`${id}-subject`}
                    className="form-input"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    maxLength={200}
                    placeholder="Brief summary"
                    style={{ width: '100%', marginTop: 4 }}
                    required
                />
            </div>
            <div>
                <label htmlFor={`${id}-body`} style={fieldLabel}>Message</label>
                <textarea
                    id={`${id}-body`}
                    className="form-input"
                    value={body}
                    onChange={e => setBody(e.target.value)}
                    maxLength={10_000}
                    rows={6}
                    placeholder="What happened? What did you expect? Any steps to reproduce?"
                    style={{ width: '100%', marginTop: 4, resize: 'vertical' }}
                    required
                />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
                {onCancel && <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>Cancel</Button>}
                <Button type="submit" disabled={submitting}>
                    {submitting ? 'Sending…' : 'Send'}
                </Button>
            </div>
        </form>
    );
}
